/**
 * 이상한 방 - 실내 평면도 검증
 *
 * `floorPlanModel.ts`의 타입·질의만 읽어 평면도 한 벌이 규약을 지키는지 확인한다.
 * 모델에서 떼어 낸 이유는 크기다 - 모델이 검증까지 안으면 540줄을 넘고, 검증은 모델의
 * 다른 어떤 함수도 필요로 하지 않는 순수 소비자라 경계가 자연스럽다.
 *
 * 최상위 상수가 없으므로 배럴(`floorPlan.ts`)이 `export *`로 내보내도 평가 순서 문제가 없다.
 *
 * 무엇을 잡는가:
 *  - 구획: id 중복 · 크기가 양의 정수 · 외벽 링 침범 · rect 겹침
 *  - 문: 폭 · 같은 구획 연결 · 없는 구획 · 구획 내부 관통 · 맞닿은 벽 위인가 · 폭이 구간을 넘는가
 *  - 창: 크기 · 없는 구획 · 벽면 위인가 · 폭 · 천장 높이
 *  - 연결성: 첫 구획에서 문으로 전부 닿는가
 */

import {
  OUTER_WALL_CELLS,
  doorwayCells,
  findRoom,
  openingSide,
  reachableRoomIds,
  rectsOverlap,
  roomAt,
  type Axis,
  type Cell2,
  type FloorPlan,
  type Room,
  type RoomId,
} from './floorPlanModel'

/** 개구부 띠가 `axis` 방향으로 방 rect 구간 안에 완전히 들어가는가. */
function openingWithinRoomSpan(room: Room, axis: Axis, cell: Cell2, width: number): boolean {
  const rect = room.rect
  const start = axis === 'x' ? cell.x : cell.z
  const spanStart = axis === 'x' ? rect.x : rect.z
  const spanSize = axis === 'x' ? rect.sizeX : rect.sizeZ

  return start >= spanStart && start + width <= spanStart + spanSize
}

function isPositiveInteger(value: number): boolean {
  return Number.isInteger(value) && value > 0
}

function validateRooms(plan: FloorPlan, issues: string[]): void {
  const seen = new Set<RoomId>()
  const maxX = plan.bounds.sizeX - OUTER_WALL_CELLS
  const maxZ = plan.bounds.sizeZ - OUTER_WALL_CELLS

  for (const room of plan.rooms) {
    if (seen.has(room.id)) issues.push(`구획 id 중복: '${room.id}'`)
    seen.add(room.id)

    /*
     * `name`은 `id`와 **글자 그대로 같아야 한다**(M11). 사람이 읽는 이름은 사전의 `room.<id>`가 주고 여기 남은 것은
     * 에디터·디버그가 읽는 키뿐인데, 같은 문자열이 두 필드에 적히면 언젠가 한쪽만 바뀐다. 지금 그것을 지키는 검사가
     * 없어 여기 못 박는다 - 이 줄이 거슬리는 날이 `Room.name`을 걷을 날이다.
     */
    if (room.name !== room.id) issues.push(`구획 '${room.id}'의 name이 id와 다르다: '${room.name}'`)

    if (!isPositiveInteger(room.rect.sizeX) || !isPositiveInteger(room.rect.sizeZ)) {
      issues.push(`구획 '${room.id}'의 크기가 양의 정수가 아니다`)
      continue
    }

    // 외벽 링을 침범하면 밀폐가 깨진다 - 방 벽면이 곧 건물 바깥면이 되어 버린다.
    const insideOuterRing =
      room.rect.x >= OUTER_WALL_CELLS &&
      room.rect.z >= OUTER_WALL_CELLS &&
      room.rect.x + room.rect.sizeX <= maxX &&
      room.rect.z + room.rect.sizeZ <= maxZ
    if (!insideOuterRing) issues.push(`구획 '${room.id}'가 외벽 링을 침범한다`)
  }

  for (let i = 0; i < plan.rooms.length; i += 1) {
    const a = plan.rooms[i]
    if (a === undefined) continue
    for (let j = i + 1; j < plan.rooms.length; j += 1) {
      const b = plan.rooms[j]
      if (b === undefined) continue
      if (rectsOverlap(a.rect, b.rect)) issues.push(`구획 '${a.id}'와 '${b.id}'의 rect가 겹친다`)
    }
  }
}

function validateDoorways(plan: FloorPlan, issues: string[]): void {
  for (const doorway of plan.doorways) {
    const label = `문 '${doorway.fromRoomId}'↔'${doorway.toRoomId}'`

    if (!isPositiveInteger(doorway.width)) {
      issues.push(`${label}의 폭이 양의 정수가 아니다`)
      continue
    }
    if (doorway.fromRoomId === doorway.toRoomId) {
      issues.push(`${label}가 같은 구획을 잇는다`)
      continue
    }

    const from = findRoom(plan, doorway.fromRoomId)
    const to = findRoom(plan, doorway.toRoomId)
    if (from === undefined || to === undefined) {
      issues.push(`${label}가 존재하지 않는 구획을 가리킨다`)
      continue
    }

    // 개구부는 벽 위에만 뚫을 수 있다. 방 내부에 뚫리면 벽 셀 계산이 방을 갉아먹는다.
    for (const cell of doorwayCells(doorway)) {
      if (roomAt(plan, cell.x, cell.z) !== undefined) {
        issues.push(`${label}의 개구부 (${cell.x}, ${cell.z})가 구획 내부에 있다`)
      }
    }

    const fromSide = openingSide(from, doorway.axis, doorway.cell)
    const toSide = openingSide(to, doorway.axis, doorway.cell)
    if (fromSide === null || toSide === null || fromSide === toSide) {
      issues.push(`${label}가 두 구획의 맞닿은 벽 위에 있지 않다`)
      continue
    }

    if (
      !openingWithinRoomSpan(from, doorway.axis, doorway.cell, doorway.width) ||
      !openingWithinRoomSpan(to, doorway.axis, doorway.cell, doorway.width)
    ) {
      issues.push(`${label}의 폭이 두 구획의 맞닿은 구간을 벗어난다`)
    }
  }
}

function validateWindows(plan: FloorPlan, issues: string[]): void {
  for (const window of plan.windows) {
    const label = `창 '${window.id}'`

    if (!isPositiveInteger(window.width) || !isPositiveInteger(window.heightCells)) {
      issues.push(`${label}의 크기가 양의 정수가 아니다`)
      continue
    }

    const room = findRoom(plan, window.roomId)
    if (room === undefined) {
      issues.push(`${label}가 존재하지 않는 구획 '${window.roomId}'를 가리킨다`)
      continue
    }

    if (openingSide(room, window.axis, window.cell) === null) {
      issues.push(`${label}가 구획 '${window.roomId}'의 벽면 위에 있지 않다`)
    }
    if (!openingWithinRoomSpan(room, window.axis, window.cell, window.width)) {
      issues.push(`${label}의 폭이 구획 벽면을 벗어난다`)
    }
    if (window.sillCell < 0 || window.sillCell + window.heightCells > plan.bounds.sizeY) {
      issues.push(`${label}가 천장 높이를 벗어난다`)
    }
  }
}

function validateConnectivity(plan: FloorPlan, issues: string[]): void {
  const first = plan.rooms[0]
  if (first === undefined) {
    issues.push('구획이 하나도 없다')
    return
  }

  const reachable = reachableRoomIds(plan, first.id)
  for (const room of plan.rooms) {
    if (!reachable.has(room.id)) issues.push(`구획 '${room.id}'에 문으로 도달할 수 없다`)
  }
}

/**
 * 평면도 전량 검증. 문제를 **모아서** 돌려준다.
 * 첫 오류에서 던지지 않는 이유: 평면도는 손으로 짜는 데이터라 한 번에 여러 곳이 어긋나는데,
 * 하나씩 고치게 만들면 수정-실행 왕복이 오류 개수만큼 늘어난다.
 */
export function validateFloorPlan(plan: FloorPlan): readonly string[] {
  const issues: string[] = []

  const bounds = plan.bounds
  if (
    !isPositiveInteger(bounds.sizeX) ||
    !isPositiveInteger(bounds.sizeY) ||
    !isPositiveInteger(bounds.sizeZ)
  ) {
    issues.push('bounds의 각 축은 1 이상의 정수여야 한다')
    return issues
  }

  validateRooms(plan, issues)
  validateDoorways(plan, issues)
  validateWindows(plan, issues)
  validateConnectivity(plan, issues)

  return issues
}

/** 검증 실패를 즉시 실패로 바꾼다. 렌더·생성기는 이걸 통과한 평면도만 받는다. */
export function assertValidFloorPlan(plan: FloorPlan): void {
  const issues = validateFloorPlan(plan)
  if (issues.length === 0) return

  throw new RangeError(`평면도 '${plan.id}'가 올바르지 않다:\n- ${issues.join('\n- ')}`)
}
